const IM_COOKIE_NAME = "cp_impact_attribution";
const IM_MAX_LIFE = 45;

(() => {
  if (!dayjs || typeof dayjs !== "function" || !dayjs_plugin_duration || typeof dayjs_plugin_duration !== "function") {
    console.error("dayjs or dayjs_plugin_duration is not available. Ensure that both are correctly imported.");
    return;
  }

  dayjs.extend(dayjs_plugin_duration);

  const { format, add } = dayjs();
  const { duration } = dayjs;

  if (!format || !add || !duration) {
    console.error("Required functions from dayjs or dayjs_plugin_duration are not available.", !format , !add , !duration);
    return;
  }

  document.addEventListener("DOMContentLoaded", () => {
    console.log("[impact module loaded]");

    handleOnPageLoad();

    // Doesn't work, seems like there's no cart update event
    // document.addEventListener("cart.updated", syncAttributionToCart);
  });

  function handleOnPageLoad() {
    const MAX_RETRIES = 100;
    const INTERVAL_DURATION = 250;

    const COOKIE_POLICY_DOMAIN = "eltamd.com";
    const COOKIE_POLICY_CATEGORY = "advertising";

    checkPrivacyManager().then(handleAttributionSession);

    function checkPrivacyManager(retries = 0) {
      return new Promise((resolve, reject) => {
        if (!window.PrivacyManagerAPI && retries < MAX_RETRIES) {
          setTimeout(() => checkPrivacyManager(retries + 1).then(resolve).catch(reject), INTERVAL_DURATION);
          return;
        }

        resolve(Boolean(window.PrivacyManagerAPI));
      });
    }

    function handleAttributionSession(hasPrivacyManager) {
      if (!hasPrivacyManager) {
        purgeAttributionSession();
        return;
      }

      window.addEventListener("message", watchConsent);
      refreshAttributionSession();
    }

    function watchConsent(event) {
      try {
        const message = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (!message || message?.source !== "preference_manager" || message?.message !== "submit_preferences") {
          return;
        }

        refreshAttributionSession();
      } catch (err) {
        console.log("Error attaching event listener to privacy consent: ", err);
      }
    }

    function refreshAttributionSession() {
      if (!window.PrivacyManagerAPI) {
        return;
      }

      const consentStatus = window.PrivacyManagerAPI.callApi(
        "getConsent",
        COOKIE_POLICY_DOMAIN,
        null,
        null,
        COOKIE_POLICY_CATEGORY
      );

      if (consentStatus?.consent && consentStatus.consent === "approved") {
        const urlParams = Object.fromEntries(new URLSearchParams(window.location.search)); // This is fine for now

        initializeAttributionCookie(urlParams);

        return;
      }

      purgeAttributionSession();
    }
  }

  function initializeAttributionCookie(query) {
    const { irclickid: clickId } = query;

    if (!clickId) {
      return;
    }

    const isCookieExists = checkCookieExists(IM_COOKIE_NAME);

    if (!isCookieExists) {
      purgeAttributionSession();
    }

    const DATE_NOW = new Date();
    const DATE_FORMAT = "dd-MMM-yyyy HH:mm:ss z";
    const loadTime = DATE_NOW.toISOString();

    // const expiryTime = format(add(DATE_NOW, { days: IM_MAX_LIFE }), DATE_FORMAT);
    // const maxAgeInSeconds = hoursToSeconds(IM_MAX_LIFE * 24);

    // Converted from date-fns to dayjs
    const expiryTime = dayjs(DATE_NOW).add(IM_MAX_LIFE, "day").format(DATE_FORMAT);
    const maxAgeInSeconds = duration(IM_MAX_LIFE * 24, "hours").asSeconds();

    setCookie(
      IM_COOKIE_NAME,
      JSON.stringify({
        clickId,
        loadTime,
        expiryTime,
      }),
      maxAgeInSeconds
    );

    syncAttributionToCart();
  }

  async function syncAttributionToCart() {
    const IM_COOKIE_VALUE = getCookieValue(IM_COOKIE_NAME);
    const { attributes } = await getCartValues();
    let isAttributeSynced = false;

    if (!IM_COOKIE_VALUE || typeof IM_COOKIE_VALUE !== "object" || !IM_COOKIE_VALUE?.clickId) {
      await purgeAttributionSession();
      return;
    }

    if (!Array.isArray(attributes)) {
      return;
    }

    const hasCookieInCart = attributes.find((attr) => {
      if (attr.key === IM_COOKIE_NAME) {
        const stringifiedCookieValue = JSON.stringify(IM_COOKIE_VALUE);

        isAttributeSynced = attr.value === stringifiedCookieValue; // Check if old value is synced first
        attr.value = stringifiedCookieValue;

        return true;
      }

      return false;
    });

    if (!hasCookieInCart) {
      attributes.push({ key: IM_COOKIE_NAME, value: JSON.stringify(IM_COOKIE_VALUE) });
    }

    if (!isAttributeSynced) {
      await updateCartAttributes({ attributes });
    }
  }

  async function purgeAttributionSession() {
    const IM_COOKIE_VALUE = getCookieValue(IM_COOKIE_NAME);
    const { attributes } = await getCartValues();

    if (IM_COOKIE_VALUE) {
      setCookie(IM_COOKIE_NAME, null, -1);
    }

    if (!Array.isArray(attributes)) {
      return;
    }

    const filteredAttributes = attributes.filter((attr) => attr?.key && attr.key !== IM_COOKIE_NAME ? attr : false );

    await updateCartAttributes({ attributes: filteredAttributes });
  }

  // ------- Helpers

  function checkCookieExists(name) {
    const cookie = getCookie(name);

    return cookie && parseCookieValue(cookie);
  }

  function parseCookieValue(cookie) {
    if (!cookie) {
      return;
    }

    const value = cookie.split("=")[1];

    try {
      return JSON.parse(decodeURIComponent(value));
    } catch (e) {
      console.error("Failed to parse cookie value:", e);
      return null;
    }
  }

  function getCookie(name) {
    return document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  }

  function getCookieValue(key) {
    try {
      return parseCookieValue(getCookie(key));
    } catch (e) {
      console.error("Failed to get cookie value:", e, e?.stack);
      return false;
    }
  }

  function setCookie(name, value, maxAge) {
    const expires = new Date(Date.now() + maxAge * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax; secure`;

    return !!getCookie(name);
  }

  async function getCartValues() {
    const cartObject = await getCart();
    let attributes = cartObject?.attributes;

    if (!cartObject || !Array.isArray(attributes)) {
      return { attributes: [] };
    }

    return { attributes };
  }

  async function getCart() {
    const cartResponse = await fetch("/cart.js");

    try {
      return await cartResponse.json();
    } catch (e) {
      console.error("Failed to fetch cart", e, e.stack);
      return false;
    }
  }

  async function updateCartAttributes(options) {
    const attributes = options?.attributes;

    if (!attributes || !Array.isArray(attributes)) {
      return false;
    }

    attributes.forEach(async (attribute) => {
      try {
        await fetchUpdateCartAttributes(attribute);
      } catch (e) {
        console.error("Failed to update cart attributes:", e, e.stack);
      }
    });

    async function fetchUpdateCartAttributes(attribute) {
      const cartResponse = await fetch(`/cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: { [attribute.key]: attribute.value },
        }),
      });

      return await cartResponse.json();
    }
  }
})();
